import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Input,
  Select,
  Tag,
  Space,
  Tooltip,
  Popover,
  Popconfirm,
  Badge,
  Typography,
  Flex,
  message,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  MailOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  UserOutlined,
  PhoneOutlined,
  EyeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getEnterprisePocs,
  deleteEnterprisePoc,
  resendPocApprovalMail,
  updatePocApprovalStatus,
} from "../../../redux/slices/enterpriseSlice";
import CustomTable from "../../../components/CustomTable/CustomTable";
import CustomButton from "../../../components/CustomButton/Button";
import NewPocModal from "./NewPocModal";
import EditPocModal from "./EditPocModal";
import "./EnterprisePocs.scss";
import PrimaryButton from "../../../components/CustomButton/PrimaryButton";

const { Text } = Typography;

const DEPARTMENT_COLORS = {
  Finance: "blue",
  "Accounts Payable": "cyan",
  Procurement: "purple",
  "Legal & Compliance": "orange",
  Operations: "green",
  "Supply Chain & Logistics": "geekblue",
  "Human Resources": "magenta",
  "Executive / Management": "gold",
  "Sales & Marketing": "lime",
};

const EnterprisePocs = ({ entId, entName }) => {
  const dispatch = useDispatch();
  const { pocsList, pocsLoading, pocsPagination } = useSelector(
    (state) => state.enterprise,
  );

  // Filters state
  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState(undefined);
  const [approvalStatus, setApprovalStatus] = useState(undefined);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Modals state
  const [newModalVisible, setNewModalVisible] = useState(false);
  const [editModalData, setEditModalData] = useState(null);
  const [viewModalData, setViewModalData] = useState(null);
  const [resendingPocId, setResendingPocId] = useState(null);

  const fetchPocs = useCallback(() => {
    if (!entId) return;
    dispatch(
      getEnterprisePocs({
        entId,
        department,
        approvalStatus,
        search,
        page,
        limit,
      }),
    );
  }, [dispatch, entId, department, approvalStatus, search, page, limit]);

  useEffect(() => {
    fetchPocs();
  }, [fetchPocs]);

  const handleDelete = useCallback(
    async (pocId) => {
      try {
        const res = await dispatch(deleteEnterprisePoc(pocId));
        if (
          res?.meta?.requestStatus === "fulfilled" ||
          res?.payload?.statusCode === 200
        ) {
          message.success("Point of Contact deleted successfully.");
          fetchPocs();
        } else {
          const errorMsg =
            res?.payload?.message || res?.payload || "Failed to delete POC";
          message.error(errorMsg);
        }
      } catch (err) {
        message.error(err?.message || "Failed to delete POC");
      }
    },
    [dispatch, fetchPocs],
  );

  const handleResendMail = useCallback(
    async (pocId) => {
      try {
        setResendingPocId(pocId);
        const res = await dispatch(resendPocApprovalMail(pocId));
        if (
          res?.meta?.requestStatus === "fulfilled" ||
          res?.payload?.statusCode === 200
        ) {
          message.success("Approval notification email resent successfully!");
          fetchPocs();
        } else {
          const errorMsg =
            res?.payload?.message ||
            res?.payload ||
            "Failed to resend approval email";
          message.error(errorMsg);
        }
      } catch (err) {
        message.error(err?.message || "Failed to resend approval email");
      } finally {
        setResendingPocId(null);
      }
    },
    [dispatch, fetchPocs],
  );

  const handleReopen = useCallback(
    async (pocId) => {
      try {
        const res = await dispatch(
          updatePocApprovalStatus({
            pocId,
            approvalStatus: "PENDING",
          }),
        );
        if (
          res?.meta?.requestStatus === "fulfilled" ||
          res?.payload?.statusCode === 200
        ) {
          message.success(
            "POC reopened to Pending. Approval email sent to the Point of Contact.",
          );
          fetchPocs();
        } else {
          const errorMsg =
            res?.payload?.message || res?.payload || "Failed to reopen POC";
          message.error(errorMsg);
        }
      } catch (err) {
        message.error(err?.message || "Failed to reopen POC");
      }
    },
    [dispatch, fetchPocs],
  );

  const columns = useMemo(
    () => [
      {
        title: "POC Name & Designation",
        key: "pocName",
        render: (_, record) => {
          const name = record.pocName || record.name;
          const designation = record.designation;
          const pocId = record.pocId;

          return (
            <div className="poc-name-cell">
              <Flex align="center" gap={8}>
                <UserOutlined style={{ color: "#0047ab" }} />
                <div>
                  <div className="name-text">{name}</div>
                  {designation && (
                    <div className="designation-text">{designation}</div>
                  )}
                  <Tag
                    style={{
                      fontSize: 10,
                      lineHeight: "14px",
                      padding: "0 4px",
                      marginTop: 2,
                    }}
                  >
                    {pocId}
                  </Tag>
                </div>
              </Flex>
            </div>
          );
        },
      },
      {
        title: "Department",
        dataIndex: "department",
        key: "department",
        render: (dept) => (
          <Tag
            color={DEPARTMENT_COLORS[dept] || "default"}
            className="department-badge"
          >
            {dept || "General"}
          </Tag>
        ),
      },
      {
        title: "Contact Details",
        key: "contact",
        render: (_, record) => {
          const email = record.pocEmail || record.email;
          const mobile = record.pocMobile || record.mobile;

          return (
            <div className="contact-cell">
              <div className="contact-item">
                <MailOutlined style={{ color: "#2563eb" }} />
                <a href={`mailto:${email}`}>{email}</a>
              </div>
              <div className="contact-item">
                <PhoneOutlined style={{ color: "#10b981" }} />
                <a href={`tel:${mobile}`}>{mobile}</a>
              </div>
            </div>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "approvalStatus",
        key: "approvalStatus",
        render: (status, record) => {
          if (record.isDeleted) {
            const deletedBy =
              record.deletedByName || record.deletedBy || "Unknown user";
            const deletedAt = record.deletedAt
              ? new Date(record.deletedAt).toLocaleString()
              : null;
            return (
              <Tooltip
                title={
                  deletedAt
                    ? `Deleted by ${deletedBy} on ${deletedAt}`
                    : `Deleted by ${deletedBy}`
                }
              >
                <Tag color="default" icon={<DeleteOutlined />}>
                  Deleted
                </Tag>
              </Tooltip>
            );
          }
          switch (status) {
            case "APPROVED":
              return (
                <Tag color="success" icon={<CheckCircleOutlined />}>
                  Approved
                </Tag>
              );
            case "REJECTED":
              return (
                <Tooltip title={record.rejectionReason || "Status rejected"}>
                  <Tag color="error" icon={<CloseCircleOutlined />}>
                    Rejected
                  </Tag>
                </Tooltip>
              );
            default:
              return (
                <Tag color="warning" icon={<ClockCircleOutlined />}>
                  Pending
                </Tag>
              );
          }
        },
      },
      ...(approvalStatus === "DELETED"
        ? [
            {
              title: "Deleted By / At",
              key: "deletedAudit",
              width: 200,
              render: (_, record) => (
                <div style={{ fontSize: 12, lineHeight: 1.4 }}>
                  <div>{record.deletedByName || record.deletedBy || "—"}</div>
                  <Text type="secondary">
                    {record.deletedAt
                      ? new Date(record.deletedAt).toLocaleString()
                      : "—"}
                  </Text>
                </div>
              ),
            },
          ]
        : []),
      {
        title: "Additional Place of Business (APOB)",
        dataIndex: "apob",
        key: "apob",
        render: (apob) =>
          apob ? (
            <Tooltip title={apob}>
              <span className="apob-tag">
                <EnvironmentOutlined style={{ marginRight: 4 }} />
                {apob}
              </span>
            </Tooltip>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              —
            </Text>
          ),
      },
      {
        title: "Location / Address",
        key: "address",
        render: (_, record) => {
          const addr = record.address;
          if (!addr || !addr.addressLine1) {
            return (
              <Text type="secondary" style={{ fontSize: 12 }}>
                —
              </Text>
            );
          }

          const popoverContent = (
            <div className="address-popover-content">
              <div>
                <strong>{addr.addressLine1}</strong>
              </div>
              {addr.addressLine2 && <div>{addr.addressLine2}</div>}
              <div>
                {[addr.city, addr.district, addr.state]
                  .filter(Boolean)
                  .join(", ")}
              </div>
              {addr.zipCode && <div>PIN: {addr.zipCode}</div>}
            </div>
          );

          return (
            <Popover
              content={popoverContent}
              title="Point of Contact Address"
              trigger="hover"
            >
              <Tag
                icon={<EnvironmentOutlined />}
                color="cyan"
                style={{ cursor: "pointer" }}
              >
                {addr.city || addr.state || "View Address"}
              </Tag>
            </Popover>
          );
        },
      },
      {
        title: "Mail Status",
        key: "mailStatus",
        render: (_, record) => {
          const sent = record.isApprovalMailSent;
          const sentAt = record.approvalMailSentAt;

          return sent ? (
            <Tooltip
              title={
                sentAt
                  ? `Mail sent on ${new Date(sentAt).toLocaleString()}`
                  : "Approval notification email sent"
              }
            >
              <Badge status="success" text="Email Sent" />
            </Tooltip>
          ) : (
            <Badge status="default" text="Not Sent" />
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        width: 140,
        fixed: "right",
        render: (_, record) => {
          const isDeleted = Boolean(record.isDeleted);
          const isPending = record.approvalStatus === "PENDING";
          const isRejected = record.approvalStatus === "REJECTED";

          if (isDeleted) {
            return (
              <Tooltip title="View deleted POC details">
                <CustomButton
                  type="text"
                  size="small"
                  className="poc-action-btn"
                  data-testid="view-deleted-poc"
                  icon={<EyeOutlined style={{ color: "#0047ab" }} />}
                  onClick={() => setViewModalData(record)}
                />
              </Tooltip>
            );
          }

          return (
            <Space size="small">
              {isPending ? (
                <Tooltip title="Edit POC Details">
                  <CustomButton
                    type="text"
                    size="small"
                    className="poc-action-btn"
                    data-testid="edit-poc"
                    icon={<EditOutlined style={{ color: "#0047ab" }} />}
                    onClick={() => setEditModalData(record)}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="View POC Details">
                  <CustomButton
                    type="text"
                    size="small"
                    className="poc-action-btn"
                    data-testid="view-poc"
                    icon={<EyeOutlined style={{ color: "#0047ab" }} />}
                    onClick={() => setViewModalData(record)}
                  />
                </Tooltip>
              )}

              {isPending && (
                <Tooltip title="Resend Approval Email">
                  <CustomButton
                    type="text"
                    size="small"
                    className="poc-action-btn"
                    data-testid="resend-approval-mail"
                    loading={resendingPocId === record.pocId}
                    icon={<MailOutlined style={{ color: "#10b981" }} />}
                    onClick={() => handleResendMail(record.pocId)}
                  />
                </Tooltip>
              )}

              {isRejected && (
                <Popconfirm
                  title="Reopen Point of Contact"
                  description="Set this rejected POC back to Pending and resend the approval email?"
                  onConfirm={() => handleReopen(record.pocId)}
                  okText="Yes, Reopen"
                  cancelText="Cancel"
                >
                  <Tooltip title="Reopen to Pending">
                    <CustomButton
                      type="text"
                      size="small"
                      className="poc-action-btn"
                      data-testid="reopen-poc"
                      icon={<ReloadOutlined style={{ color: "#6366f1" }} />}
                    />
                  </Tooltip>
                </Popconfirm>
              )}

              <Popconfirm
                title="Delete Point of Contact"
                description={`Are you sure you want to delete POC "${record.pocName || record.name}"?`}
                onConfirm={() => handleDelete(record.pocId)}
                okText="Yes, Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Tooltip title="Delete POC">
                  <CustomButton
                    type="text"
                    size="small"
                    danger
                    className="poc-action-btn"
                    data-testid="delete-poc"
                    icon={<DeleteOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    [resendingPocId, handleDelete, handleResendMail, handleReopen, approvalStatus],
  );

  return (
    <div className="enterprise-pocs-container">
      {/* Toolbar */}
      <Flex justify="space-between" align="center" className="pocs-toolbar">
        <Space wrap size="middle">
          <Input
            placeholder="Search Name, Email, APOB..."
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            allowClear
            className="search-input"
          />

          <Select
            placeholder="Filter Department"
            allowClear
            value={department}
            onChange={(val) => {
              setDepartment(val);
              setPage(1);
            }}
            className="filter-select"
            options={[
              { label: "All Departments", value: "" },
              { label: "Finance", value: "Finance" },
              { label: "Accounts Payable", value: "Accounts Payable" },
              { label: "Procurement", value: "Procurement" },
              { label: "Legal & Compliance", value: "Legal & Compliance" },
              { label: "Operations", value: "Operations" },
              { label: "Supply Chain", value: "Supply Chain & Logistics" },
              { label: "Human Resources", value: "Human Resources" },
              { label: "Management", value: "Executive / Management" },
            ]}
          />

          <Select
            placeholder="Filter Status"
            allowClear
            value={approvalStatus}
            onChange={(val) => {
              setApprovalStatus(val);
              setPage(1);
            }}
            className="filter-select"
            options={[
              { label: "All Statuses", value: "" },
              { label: "Pending", value: "PENDING" },
              { label: "Approved", value: "APPROVED" },
              { label: "Rejected", value: "REJECTED" },
              { label: "Deleted", value: "DELETED" },
            ]}
          />
        </Space>

        <PrimaryButton
          icon={<PlusOutlined />}
          onClick={() => setNewModalVisible(true)}
        >
          Add Point of Contact
        </PrimaryButton>
      </Flex>

      {/* Table */}
      <CustomTable
        dataSource={pocsList}
        columns={columns}
        rowKey="pocId"
        loading={pocsLoading}
        scroll={{ x: "max-content" }}
        pagination={{
          current: page,
          pageSize: limit,
          total: pocsPagination.totalRecords,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} Points of Contact`,
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setLimit(newPageSize);
          },
        }}
      />

      {/* Add Modal */}
      {newModalVisible && (
        <NewPocModal
          visible={newModalVisible}
          onCancel={() => setNewModalVisible(false)}
          enterpriseId={entId}
          enterpriseName={entName}
        />
      )}

      {/* Edit Modal */}
      {Boolean(editModalData) && (
        <EditPocModal
          visible={Boolean(editModalData)}
          onCancel={() => setEditModalData(null)}
          pocData={editModalData}
          enterpriseId={entId}
        />
      )}

      {/* View Modal (read-only for Approved/Rejected POCs) */}
      {Boolean(viewModalData) && (
        <EditPocModal
          visible={Boolean(viewModalData)}
          onCancel={() => setViewModalData(null)}
          pocData={viewModalData}
          enterpriseId={entId}
          readOnly
        />
      )}
    </div>
  );
};

export default EnterprisePocs;
